import { channelInfo, dealwithChannelChartKey } from '@op/components/ChannelLabel';
import { formatChartParamsDate, listTimeTitleFunc } from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import { channelColorMap, chartAllColors, NO_DATE } from '@op/constants';
import useLanguage from '@op/hooks/useLanguage';
import { echartsContent } from '@op/utils';
import { getXAxisAxisLabel } from '@op/utils/chart';
import { DBO_EC, DBO_Y } from '@op/utils/format';
import { dealWithOthersName } from '@op/utils/languageValue';
import React, { useMemo } from 'react';
import { fillNullWithZero, getSpecialLabelName } from '../../utils';
import { reviewsFormat } from '@op/utils/value';
import { IRealtimeCommunityChart } from '../../type';

export default function Chart(props: IRealtimeCommunityChart) {
  const { chartType, data, formatValue = reviewsFormat, title, isPercent, filter, byType, height = 366, valueKey } = props;
  const { formatMessage } = useLanguage();
  const chartRealType = useMemo(() => {
    return chartType === 'stack_line' ? 'line' : chartType;
  }, [chartType]);
  const options = useMemo(() => {
    const { series: seriesData, legend, xAxis } = data || {};
    const series = (seriesData || []).map((val, index) => {
      const { name, data: valData } = val;
      const color = channelColorMap[name] || chartAllColors[index];
      const item = { ...val };
      if (['bar', 'stack_line'].includes(chartType)) {
        item.stack = 'Total';
        if (chartType === 'stack_line') {
          item.areaStyle = DBO_EC.gradientColor(color, 0, 0.4);
        }
      }

      return {
        ...item,
        data: fillNullWithZero(valData),
        type: chartRealType,
        color
      };
    });

    return {
      series,
      legend: {
        data: legend,
        formatter: (params: any) => {
          if (byType === 'by_channel' && params !== 'Others') {
            const { name } = channelInfo(params);
            return `{${dealwithChannelChartKey(params)}|} {name| ${name}}`;
          }
          if (byType === 'by_language') {
            return dealWithOthersName(params, 'language');
          }
          const label = getSpecialLabelName(params);
          return formatMessage({ id: label });
        },
      },
      tooltip: {
        className: 'dbo-mentions-sentiment-box-tooltip-wrapper',
        formatter: (params: any) => {
          const leftTitle = listTimeTitleFunc(params[0]?.axisValue, filter?.date_type || 'hourly');
          return formatChartParamsDate({
            title: leftTitle,
            params,
            isSort: true,
            listTitleFunc: (item: any) => {
              const isNoOther = item.seriesName !== 'Others';
              if (byType === 'by_channel') {
                const { img, name } = channelInfo(item.seriesName);
                return `<span class="dbo-chart-formatter-img"><img src=${img} class="chart-common-formatter-img" /> ${isNoOther ? name : formatMessage({ id: 'Others' })}<span>`;
              }
              if (byType === 'by_language') {
                return dealWithOthersName(item.seriesName, 'language');
              }
              const label = getSpecialLabelName(item.seriesName);
              return formatMessage({ id: label });
            },
            valueFormat(value) {
              return formatValue(value);
            },
            dealwithRecords: (records, total) => {
              const list = records.map((item) => {
                if (item.originValue === 0) return item;

                if (isPercent) return item;

                return {
                  ...item,
                  value: `${item.value}`,
                };
              });

              if (!isPercent && byType !== 'in_total') {
                let realTotal = total;
                if (valueKey === 'avg_sentiment') {
                  realTotal = total / records?.length;
                }
                list.unshift({
                  text: formatMessage({ id: title || NO_DATE.EMPTY }),
                  value: formatValue(realTotal) as any,
                  originValue: realTotal,
                  originText: title as any,
                });
              }
              return list;
            },
          });
        },
      },
      xAxis: {
        axisLabel: getXAxisAxisLabel(filter),
        data: xAxis || [],
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return DBO_Y.reviewsPercent(value, isPercent);
            },
            color: '#838697',
          },
          nameTextStyle: {
            color: '#444444',
            fontWeight: 400,
            fontSize: '12px',
          },
          splitNumber: 5,
        },
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            color: '#838697',
          },
        },
      ],
      grid: {
        left: 20,
        right: 20
      }
    };
  }, [data, byType, chartType, filter, formatValue, title, isPercent, formatMessage]);

  return (
    <EChartCommon
      optionsProps={{ type: chartRealType }}
      height={height}
      options={options}
    />
  );
}